import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import DateRangePicker from "./DateRangePicker";
import AdminLineChart from "./AdminLineChart";
import api from "../../services/api";
import dayjs from "dayjs";

const QUICK_OPTIONS = [
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 7 Days", value: "last_7" },
  { label: "This Year", value: "this_year" },
  { label: "Custom", value: "custom" },
];

const AdminDashboardSummary = () => {
  const [dateRangeOption, setDateRangeOption] = useState("this_month");
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [stats, setStats] = useState(null);
  const [trendData, setTrendData] = useState(null); // updated from []

  const getRangeFromOption = (option) => {
    const today = dayjs();
    switch (option) {
      case "this_month":
        return { start: today.startOf("month"), end: today.endOf("month") };
      case "last_month":
        return {
          start: today.subtract(1, "month").startOf("month"),
          end: today.subtract(1, "month").endOf("month"),
        };
      case "last_7":
        return { start: today.subtract(7, "day"), end: today };
      case "this_year":
        return { start: today.startOf("year"), end: today.endOf("year") };
      default:
        return customRange;
    }
  };

  useEffect(() => {
    const { start, end } = getRangeFromOption(dateRangeOption);

    const fetchStats = async () => {
      try {
        const res = await api.get("/admin/stats/summary", {
          params: {
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          },
        });
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch stats", err);
      }
    };

    const fetchTrendData = async () => {
      try {
        const res = await api.get("/admin/stats/trends", {
          params: {
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          },
        });

        // Defensive check
        const raw = res.data;
        if (!Array.isArray(raw)) throw new Error("Trend data is not an array");

        const formatted = raw.map((entry) => ({
          label: entry._id,
          orders: entry.totalOrders,
          revenue: parseFloat(entry.totalRevenue?.toFixed(2) || 0),
        }));

        setTrendData(formatted);
      } catch (err) {
        console.warn("Failed to fetch trend data", err);
        setTrendData(null);
      }
    };

    if (
      dateRangeOption !== "custom" ||
      (customRange.start && customRange.end)
    ) {
      fetchStats();
      fetchTrendData();
    }
  }, [dateRangeOption, customRange]);

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Business Summary
      </Typography>

      <Grid container spacing={2} alignItems="center" mb={3}>
        <Grid item>
          <FormControl size="small">
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRangeOption}
              label="Date Range"
              onChange={(e) => setDateRangeOption(e.target.value)}
              sx={{ minWidth: 160 }}>
              {QUICK_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {dateRangeOption === "custom" && (
          <Grid item>
            <DateRangePicker
              value={customRange}
              onChange={setCustomRange}
              disabled={dateRangeOption !== "custom"}
            />
          </Grid>
        )}
      </Grid>

      {stats && (
        <>
          <Grid container wrap="wrap" sx={{ maxWidth: 1600, margin: "0 auto" }}>
            <StatCard
              label="Total Orders"
              value={stats.totalOrders}
              color="#1e88e5"
            />
            <StatCard
              label="Total Revenue"
              value={`$${stats.totalRevenue.toFixed(2)}`}
              color="#43a047"
            />
            <StatCard
              label="Average Order Value"
              value={`$${stats.avgOrderValue?.toFixed(2) || "—"}`}
              color="#6d4c41"
            />
            <StatCard
              label="Total Items Sold"
              value={stats.totalItemsSold}
              color="#5e35b1"
            />
            <StatCard
              label="Most Active Pickup Location"
              value={stats.topPickupLocation || "—"}
              color="#00897b"
            />
            <StatCard
              label="Most Active Day of Week"
              value={stats.topDayOfWeek || "—"}
              color="#3949ab"
            />
            <StatCard
              label="Most Sold Accessory"
              value={stats.topAccessory || "—"}
              color="#c62828"
            />
            <StatCard
              label="Top Upsell Item"
              value={stats.topUpsell || "—"}
              color="#ef6c00"
            />
            <StatCard
              label="Revenue Growth"
              value={
                stats.revenueGrowth != null
                  ? `${stats.revenueGrowth.toFixed(1)}%`
                  : "—"
              }
              color="#2e7d32"
            />
            <StatCard
              label="Order Growth"
              value={
                stats.orderGrowth != null
                  ? `${stats.orderGrowth.toFixed(1)}%`
                  : "—"
              }
              color="#1565c0"
            />
            <StatCard
              label="Top Repeat Customer"
              value={stats.topCustomer || "—"}
              color="#6a1b9a"
            />
            <StatCard
              label="Logged-in vs Guest Ratio"
              value={stats.userVsGuestRatio || "—"}
              color="#ad1457"
            />
          </Grid>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Standard Cakes Sold (Ascending)
            </Typography>
            <Paper elevation={1} sx={{ p: 2 }}>
              <List dense>
                {(stats.standardCakesSoldAscending || []).map((entry, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={entry.displayName || "Unnamed"}
                      secondary={`Sold: ${entry.totalSold}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </>
      )}

      {/* Line Chart Section */}
      {Array.isArray(trendData) && trendData.length > 0 ? (
        <Box mt={4}>
          <AdminLineChart data={trendData} />
        </Box>
      ) : trendData === null ? (
        <Typography variant="body2" color="text.secondary" mt={4}>
          You do not have permission to view trend data.
        </Typography>
      ) : null}
    </Box>
  );
};

const StatCard = ({ label, value, color }) => (
  <Grid item xs={12} sm={6} md={4} lg={3} sx={{ minWidth: 260, flexGrow: 1 }}>
    <Paper
      elevation={3}
      sx={{
        px: 2,
        py: 4,
        mb: 2,
        mr: { xs: 0, sm: 3 },
        borderLeft: `6px solid ${color}`,
        borderRadius: 2,
        background: "#fff",
      }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{ wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Paper>
  </Grid>
);

export default AdminDashboardSummary;
